import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"
// import { useDictionary } from "contexts/Dictionary"
import { a11yOutline } from "css/a11y"
import getMediaQuery from "css/breakpoints"
import { useLightBox } from "./context"
import { observeResize, setProperty } from "components/config-launcher/utils"

const PageWrapper = styled.div`
  position: relative;
  grid-row: 1/-1;
  grid-column: main;
  width: 100%;
  align-self: end;
  justify-self: center;
  position: relative;
  height: fit-content;
  margin-block-end: 2.5rem;
  display: grid;
  justify-content: center;
  position: sticky;
  bottom: 2.5rem;
  z-index: 1;
  //  overflow: hidden;
  --cfgl-page-ratio: 1;

  ${getMediaQuery("m")} {
    margin-block-end: 5rem;
  }
`

const PageContainer = styled.ul`
  position: relative;
  display: flex;
  gap: 12px;
  border: none;
  padding: 0;
  margin: 0;
  transform: scaleX(var(--cfgl-page-ratio));

  @media (prefers-contrast: more) {
    background: #fff;
    padding: 20px 10px;
  }
`

const PageGroup = styled.li`
  display: flex;
  & input.focus-visible + label {
    ${a11yOutline}
  }
`

const PageRadio = styled.input`
  width: 0;
  height: 0;
  appearance: none;
`

const Label = styled(motion.label)`
  position: relative;
  width: 2.5rem;
  height: 0.25rem;
  border-radius: 5px;
  opacity: 0.4;
  border: none;
  padding: 0;
  pointer-events: all;
  text-align: center;
  font-size: clamp(0.875rem, 0.8125rem + 0.1563vw, 1rem); // 14px > 16px
  line-height: 1;
  transition: background 0.3s;

  .dark-theme & {
    background: white;
  }
  .light-theme & {
    background: black;
  }

  & > span {
    position: absolute;
    top: 0;
    font-size: 0.8em;
    white-space: nowrap;
    padding-block: 0 0.7rem;
    transform: translateX(-50%) translateY(-100%) scaleX(calc(1 / var(--cfgl-page-ratio)));
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
  }

  @media (prefers-contrast: more) {
    background: black;
  }

  @media (hover: hover) and (pointer: fine) {
    &.unchecked:hover {
      cursor: pointer;
    }
    &:hover {
      & > span {
        opacity: 1;
      }
    }
  }

  &.unchecked::after {
    content: "";
    position: absolute;
    top: -12px;
    bottom: -12px;
    left: -4px;
    right: -4px;
  }
`

function Page({ id, index, num }) {
  // const dictionary = useDictionary()
  const { currentIndex } = useLightBox()
  const ref = useRef()
  const [active, setActive] = useState(currentIndex.get() === index)

  function onSlideChange(i) {
    setActive(index === i)
  }
  useEffect(() => currentIndex.onChange(onSlideChange), [])

  const variants = {
    checked: { width: "6.25rem", opacity: 1 },
    unchecked: { width: "2.5rem", opacity: 0.4 },
  }

  return (
    <PageGroup aria-current={active}>
      <PageRadio ref={ref} type='radio' name='cfgl-page-control' id={`cfgl-page-control-${index}`} value={index} checked={active} />
      <Label
        htmlFor={`cfgl-page-control-${index}`}
        variants={variants}
        initial={active ? "checked" : "unchecked"}
        animate={active ? "checked" : "unchecked"}
        whileHover={{ opacity: 1 }}
        className={active ? "checked" : "unchecked"}
        transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}
      ></Label>
    </PageGroup>
  )
}

export default function Pagination() {
  const { lightBoxes, targetSlide } = useLightBox()
  const rfmain = useRef(null)
  const rcontainer = useRef(null)

  function onResize(isize) {
    const fsw = rcontainer.current.offsetWidth
    const ratio = Math.min(1, isize / fsw)
    setProperty(rfmain.current, "--cfgl-page-ratio", ratio)
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize), [])

  const onChange = e => targetSlide.set(Number(e.target.value))

  return (
    <PageWrapper ref={rfmain}>
      <PageContainer ref={rcontainer} onChange={onChange}>
        {Array.from(lightBoxes).map(([id], i) => (
          <Page key={`page-${id}`} id={`page-${id}`} index={i} num={lightBoxes.size} />
        ))}
      </PageContainer>
    </PageWrapper>
  )
}
